import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import ClearRoundedIcon from "@mui/icons-material/ClearRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";

import {
  useLocation,
  useNavigate,
} from "react-router-dom";

import RetailAPI from "../../../../api/Retail";
import {
  getLangFromCookie,
} from "../../../../Utils/LangCookie";
import {
  resolveWordPressUserID,
} from "../../../../Utils/UserIdentity";
import { useAuth } from "../../Auth/AuthContext";

const FALLBACK_FONTS = {
  ZH: "LINE Seed TW",
  EN: "LINE Seed Sans",
  JP: "LINE Seed JP",
};

const I18N = {
  title: {
    ZH: "報修紀錄",
    EN: "Repair History",
    JP: "修理履歴",
  },

  loading: {
    ZH: "載入報修紀錄中…",
    EN: "Loading repair history…",
    JP: "修理履歴を読み込んでいます…",
  },

  empty: {
    ZH: "目前沒有報修紀錄。",
    EN: "No repair history yet.",
    JP: "修理履歴はまだありません。",
  },

  loadFailed: {
    ZH: "載入報修紀錄失敗，請稍後再試。",
    EN:
      "Unable to load repair history. Please try again later.",
    JP:
      "修理履歴を読み込めませんでした。しばらくしてからもう一度お試しください。",
  },

  table: {
    retailID: {
      ZH: "報修編號",
      EN: "Repair ID",
      JP: "修理受付番号",
    },

    equipment: {
      ZH: "設備名稱",
      EN: "Equipment",
      JP: "設備名",
    },

    model: {
      ZH: "設備型號",
      EN: "Model",
      JP: "設備型式",
    },

    serial: {
      ZH: "設備序號",
      EN: "Serial Number",
      JP: "シリアル番号",
    },

    submittedAt: {
      ZH: "送出日期",
      EN: "Submitted At",
      JP: "送信日時",
    },

    status: {
      ZH: "狀態",
      EN: "Status",
      JP: "ステータス",
    },

    actions: {
      ZH: "操作",
      EN: "Actions",
      JP: "操作",
    },
  },

  status: {
    checkedout: {
      ZH: "已送出",
      EN: "Submitted",
      JP: "送信済み",
    },

    processing: {
      ZH: "處理中",
      EN: "Processing",
      JP: "処理中",
    },

    completed: {
      ZH: "已完成",
      EN: "Completed",
      JP: "完了",
    },

    canceled: {
      ZH: "已取消",
      EN: "Canceled",
      JP: "キャンセル済み",
    },

    unknown: {
      ZH: "未知",
      EN: "Unknown",
      JP: "不明",
    },
  },

  search: {
    placeholder: {
      ZH: "搜尋：報修編號／設備／型號／序號",
      EN:
        "Search: ID / equipment / model / serial",
      JP:
        "検索：受付番号／設備／型式／シリアル番号",
    },

    clear: {
      ZH: "清除",
      EN: "Clear",
      JP: "クリア",
    },
  },

  filter: {
    status: {
      ZH: "狀態",
      EN: "Status",
      JP: "ステータス",
    },

    allStatus: {
      ZH: "全部狀態",
      EN: "All Statuses",
      JP: "すべて",
    },

    dateFrom: {
      ZH: "開始日期",
      EN: "From",
      JP: "開始日",
    },

    dateTo: {
      ZH: "結束日期",
      EN: "To",
      JP: "終了日",
    },
  },

  actions: {
    view: {
      ZH: "查看",
      EN: "View",
      JP: "詳細",
    },

    cancel: {
      ZH: "取消報修",
      EN: "Cancel Repair",
      JP: "修理依頼をキャンセル",
    },

    close: {
      ZH: "關閉",
      EN: "Close",
      JP: "閉じる",
    },
  },

  cancel: {
    title: {
      ZH: "取消報修",
      EN: "Cancel Repair Request",
      JP: "修理依頼をキャンセル",
    },

    message: {
      ZH:
        "確定要取消此報修申請嗎？取消後將無法由會員端恢復。",
      EN:
        "Are you sure you want to cancel this repair request? It cannot be restored from the member page.",
      JP:
        "この修理依頼をキャンセルしてもよろしいですか？会員ページから元に戻すことはできません。",
    },

    confirm: {
      ZH: "確認取消",
      EN: "Confirm Cancel",
      JP: "キャンセルを確定",
    },

    failed: {
      ZH:
        "取消報修失敗，請重新整理頁面後再試。",
      EN:
        "Unable to cancel the repair request. Refresh the page and try again.",
      JP:
        "修理依頼をキャンセルできませんでした。ページを更新してもう一度お試しください。",
    },
  },

  dialog: {
    title: {
      ZH: "報修詳細",
      EN: "Repair Details",
      JP: "修理詳細",
    },

    requestSection: {
      ZH: "報修資訊",
      EN: "Request Information",
      JP: "修理依頼情報",
    },

    contactSection: {
      ZH: "聯絡資料",
      EN: "Contact Information",
      JP: "連絡先情報",
    },

    equipmentSection: {
      ZH: "設備資料",
      EN: "Equipment Information",
      JP: "設備情報",
    },

    faultSection: {
      ZH: "故障資料",
      EN: "Fault Information",
      JP: "故障情報",
    },

    commentsSection: {
      ZH: "報修留言紀錄",
      EN: "Repair Comments",
      JP: "修理コメント履歴",
    },

    commentsLoading: {
      ZH: "載入留言中…",
      EN: "Loading comments…",
      JP: "コメントを読み込んでいます…",
    },

    commentsEmpty: {
      ZH: "目前沒有報修留言。",
      EN: "There are no comments yet.",
      JP: "コメントはまだありません。",
    },

    commentsLoadFailed: {
      ZH: "載入報修留言失敗，請稍後再試。",
      EN:
        "Unable to load comments. Please try again later.",
      JP:
        "コメントを読み込めませんでした。しばらくしてからもう一度お試しください。",
    },

    fields: {
      retailID: {
        ZH: "報修編號",
        EN: "Repair ID",
        JP: "修理受付番号",
      },

      status: {
        ZH: "目前狀態",
        EN: "Current Status",
        JP: "現在の状態",
      },

      createdAt: {
        ZH: "建立時間",
        EN: "Created At",
        JP: "受付日時",
      },

      updatedAt: {
        ZH: "最後更新時間",
        EN: "Last Updated",
        JP: "最終更新日時",
      },

      name: {
        ZH: "報修人姓名",
        EN: "Requester",
        JP: "修理依頼者名",
      },

      usageLocation: {
        ZH: "使用據點／門市／廠別",
        EN: "Usage Site / Store / Factory",
        JP: "使用拠点／店舗／工場",
      },

      endUser: {
        ZH: "末端使用者",
        EN: "End User",
        JP: "エンドユーザー",
      },

      repairAvailableTime: {
        ZH: "可維修時段",
        EN: "Available Repair Time",
        JP: "修理対応可能時間",
      },

      landline: {
        ZH: "市話",
        EN: "Landline",
        JP: "固定電話",
      },

      mobile: {
        ZH: "手機號碼",
        EN: "Mobile Number",
        JP: "携帯電話番号",
      },

      email: {
        ZH: "Email",
        EN: "Email",
        JP: "メールアドレス",
      },

      address: {
        ZH: "完整地址",
        EN: "Full Address",
        JP: "住所",
      },

      equipmentName: {
        ZH: "設備名稱",
        EN: "Equipment Name",
        JP: "設備名",
      },

      customEquipment: {
        ZH: "自訂設備名稱或型號",
        EN: "Custom Equipment Name or Model",
        JP: "入力した設備名または型式",
      },

      modelName: {
        ZH: "設備型號",
        EN: "Equipment Model",
        JP: "設備型式",
      },

      serialNumber: {
        ZH: "設備序號",
        EN: "Serial Number",
        JP: "シリアル番号",
      },

      purchaseDate: {
        ZH: "採購日期",
        EN: "Purchase Date",
        JP: "購入日",
      },

      shippingDate: {
        ZH: "出貨日期",
        EN: "Shipping Date",
        JP: "出荷日",
      },

      installationDate: {
        ZH: "安裝日期",
        EN: "Installation Date",
        JP: "設置日",
      },

      installer: {
        ZH: "安裝單位／安裝人員",
        EN:
          "Installation Company / Installer",
        JP: "設置会社／設置担当者",
      },

      installationSource: {
        ZH: "安裝來源",
        EN: "Installation Source",
        JP: "設置元",
      },

      customInstallationSource: {
        ZH: "其他安裝來源",
        EN: "Other Installation Source",
        JP: "その他の設置元",
      },

      faultReason: {
        ZH: "故障原因",
        EN: "Fault Reason",
        JP: "故障原因",
      },

      customFaultReason: {
        ZH: "其他說明",
        EN: "Other Details",
        JP: "その他の説明",
      },

      repairNotes: {
        ZH: "備註",
        EN: "Notes",
        JP: "備考",
      },
    },
  },
};

const EQUIPMENT_LABELS = {
  soap_dispenser: {
    ZH: "給皂機",
    EN: "Soap Dispenser",
    JP: "ソープディスペンサー",
  },

  sanitizer_dispenser: {
    ZH: "消毒機",
    EN: "Sanitizer Dispenser",
    JP: "消毒液ディスペンサー",
  },

  dyson: {
    ZH: "dyson",
    EN: "dyson",
    JP: "dyson",
  },

  equipment: {
    ZH: "設備",
    EN: "Equipment",
    JP: "設備",
  },

  insect_light: {
    ZH: "捕蟲燈",
    EN: "Insect Light",
    JP: "捕虫器",
  },

  other: {
    ZH: "其他",
    EN: "Other",
    JP: "その他",
  },
};

const INSTALLATION_SOURCE_LABELS = {
  manufacturer: {
    ZH: "原廠",
    EN: "Manufacturer",
    JP: "メーカー",
  },

  self_installed: {
    ZH: "自行安裝",
    EN: "Self-installed",
    JP: "自己設置",
  },

  distributor: {
    ZH: "經銷商",
    EN: "Distributor",
    JP: "販売代理店",
  },

  contractor: {
    ZH: "工程商",
    EN: "Contractor",
    JP: "施工業者",
  },

  other: {
    ZH: "其他",
    EN: "Other",
    JP: "その他",
  },
};

const FAULT_REASON_LABELS = {
  sensor_problem: {
    ZH: "感應不良",
    EN: "Sensor malfunction",
    JP: "センサー不良",
  },

  indicator_light_problem: {
    ZH: "燈號異常",
    EN: "Indicator light malfunction",
    JP: "表示ランプの異常",
  },

  no_response_with_power_or_battery: {
    ZH: "插電或已裝電池無反應",
    EN:
      "No response when plugged in or with batteries installed",
    JP:
      "電源接続または電池装着後も反応しない",
  },

  damaged_outer_case: {
    ZH: "外殼受損",
    EN: "Damaged outer casing",
    JP: "外装ケースの損傷",
  },

  damaged_battery_box: {
    ZH: "電池盒受損",
    EN: "Damaged battery compartment",
    JP: "電池ボックスの損傷",
  },

  damaged_power_plug: {
    ZH: "插頭受損",
    EN: "Damaged power plug",
    JP: "電源プラグの損傷",
  },

  damaged_power_adapter: {
    ZH: "變壓器受損",
    EN: "Damaged power adapter",
    JP: "電源アダプターの損傷",
  },

  equipment_moisture_damage: {
    ZH: "機台受潮",
    EN: "Equipment affected by moisture",
    JP: "機器の湿気・水分による異常",
  },

  damaged_tray: {
    ZH: "托盤受損",
    EN: "Damaged tray",
    JP: "トレーの損傷",
  },

  missing_parts: {
    ZH: "零件缺少",
    EN: "Missing parts",
    JP: "部品の欠品",
  },

  missing_key: {
    ZH: "鑰匙遺失",
    EN: "Missing key",
    JP: "鍵の紛失",
  },

  dirty_equipment: {
    ZH: "機台髒污",
    EN: "Dirty equipment",
    JP: "機器の汚れ",
  },

  detergent_not_dispensed: {
    ZH: "洗劑無法正常抽取",
    EN:
      "Detergent cannot be dispensed normally",
    JP:
      "洗剤を正常に吸い上げられない",
  },

  lamp_not_lighting: {
    ZH: "燈管不亮",
    EN: "Lamp does not light",
    JP: "ランプが点灯しない",
  },

  lamp_flickering: {
    ZH: "燈管閃爍",
    EN: "Lamp flickers",
    JP: "ランプが点滅する",
  },

  equipment_overheating: {
    ZH: "機身發燙",
    EN: "Equipment body becomes hot",
    JP: "本体が過熱する",
  },

  abnormal_noise: {
    ZH: "異音",
    EN: "Abnormal noise",
    JP: "異音",
  },

  other_problem: {
    ZH: "其他",
    EN: "Other",
    JP: "その他",
  },
};

function normalizeLang(value) {
  const lang = String(value || "")
    .trim()
    .toUpperCase();

  return lang === "EN" || lang === "JP"
    ? lang
    : "ZH";
}

function safe(value) {
  return value == null ||
    String(value).trim() === ""
    ? "-"
    : String(value);
}

function translatedLabel(
  catalog,
  value,
  lang,
) {
  const key = String(value || "").trim();
  const labels = catalog[key];

  if (!labels) {
    return safe(key);
  }

  return labels[lang] || labels.ZH || safe(key);
}

function statusLabel(lang, status) {
  const labels =
    I18N.status[status] ||
    I18N.status.unknown;

  return labels[lang] || labels.ZH;
}

function statusChipColor(status) {
  if (status === "checkedout") {
    return "info";
  }

  if (status === "processing") {
    return "warning";
  }

  if (status === "completed") {
    return "success";
  }

  if (status === "canceled") {
    return "error";
  }

  return "default";
}

function parseMysqlDateTime(value) {
  const text = String(value || "").trim();

  const match = text.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?/,
  );

  if (!match) {
    return null;
  }

  const date = new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
    Number(match[4] || 0),
    Number(match[5] || 0),
    Number(match[6] || 0),
  );

  return Number.isNaN(date.getTime())
    ? null
    : date;
}

function formatDateTime(value) {
  const date = parseMysqlDateTime(value);

  if (!date) {
    return safe(value);
  }

  const pad = (number) =>
    String(number).padStart(2, "0");

  return [
    date.getFullYear(),
    "/",
    pad(date.getMonth() + 1),
    "/",
    pad(date.getDate()),
    " ",
    pad(date.getHours()),
    ":",
    pad(date.getMinutes()),
  ].join("");
}

function formatDateOnly(value) {
  const text = String(value || "").trim();

  const match = text.match(
    /^(\d{4})-(\d{2})-(\d{2})$/,
  );

  return match
    ? `${match[1]}/${match[2]}/${match[3]}`
    : safe(text);
}

function buildLandline(person) {
  const area = String(
    person?.landlineAreaCode || "",
  ).trim();

  const number = String(
    person?.landlinePhoneNumber || "",
  ).trim();

  if (!area && !number) {
    return "";
  }

  if (!area) {
    return number;
  }

  if (!number) {
    return area;
  }

  return `${area}-${number}`;
}

function buildAddress(person) {
  return [
    person?.city,
    person?.district,
    person?.street,
    person?.lane,
    person?.alley,
    person?.streetNumber,
    person?.floor,
    person?.otherAddress,
  ]
    .map((value) =>
      String(value || "").trim(),
    )
    .filter(Boolean)
    .join(" ");
}

function displayEquipment(item, lang) {
  const equipmentName = String(
    item?.info?.equipmentName || "",
  ).trim();

  const custom = String(
    item?.info
      ?.customEquipmentNameOrModel || "",
  ).trim();

  if (
    equipmentName === "other" &&
    custom
  ) {
    return custom;
  }

  return translatedLabel(
    EQUIPMENT_LABELS,
    equipmentName,
    lang,
  );
}

function displayModel(item) {
  return (
    String(
      item?.info?.modelName || "",
    ).trim() ||
    String(
      item?.info
        ?.customEquipmentNameOrModel || "",
    ).trim()
  );
}

function KV({
  label,
  value,
  fontFamily,
  multiline = false,
  children,
}) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        sx={{
          mb: 0.5,
          color: "text.secondary",
          fontFamily,
          fontSize: 13,
          fontWeight: 700,
          letterSpacing: 0.2,
        }}
      >
        {label}
      </Typography>

      {children || (
        <Typography
          sx={{
            fontFamily,
            fontSize: 15,
            fontWeight: 600,
            overflowWrap: "anywhere",
            whiteSpace: multiline
              ? "pre-wrap"
              : "normal",
          }}
        >
          {safe(value)}
        </Typography>
      )}
    </Box>
  );
}

function DetailSection({
  title,
  fontFamily,
  children,
}) {
  return (
    <Box sx={{ mb: 2.5 }}>
      <Typography
        sx={{
          mb: 1,
          fontFamily,
          fontSize: 17,
          fontWeight: 800,
        }}
      >
        {title}
      </Typography>

      <Paper
        variant="outlined"
        sx={{
          borderRadius: 2,
          bgcolor: "rgba(0,0,0,0.015)",
          p: {
            xs: 1.75,
            sm: 2,
          },
        }}
      >
        {children}
      </Paper>
    </Box>
  );
}

function MobileRowCard({
  row,
  lang,
  fontFamily,
  onView,
  onCancel,
}) {
  const status =
    row?.status || "unknown";

  const Line = ({
    label,
    value,
    strong = false,
  }) => (
    <Stack
      direction="row"
      spacing={0.75}
      alignItems="flex-start"
    >
      <Typography
        sx={{
          flexShrink: 0,
          color: "text.secondary",
          fontFamily,
          fontSize: 12,
          fontWeight: 700,
        }}
      >
        {label}：
      </Typography>

      <Typography
        sx={{
          minWidth: 0,
          fontFamily,
          fontSize: 13,
          fontWeight: strong
            ? 700
            : 500,
          overflowWrap: "anywhere",
        }}
      >
        {safe(value)}
      </Typography>
    </Stack>
  );

  return (
    <Paper
      variant="outlined"
      sx={{
        display: "grid",
        gridTemplateColumns:
          "minmax(0, 1fr) auto",
        alignItems: "start",
        gap: 1.25,
        borderRadius: 2,
        p: 1.5,
      }}
    >
      <Stack
        spacing={0.7}
        sx={{ minWidth: 0 }}
      >
        <Line
          label={
            I18N.table.retailID[lang]
          }
          value={row?.retailID}
          strong
        />

        <Line
          label={
            I18N.table.equipment[lang]
          }
          value={displayEquipment(
            row,
            lang,
          )}
        />

        <Line
          label={I18N.table.model[lang]}
          value={displayModel(row)}
        />

        <Line
          label={I18N.table.serial[lang]}
          value={
            row?.info?.serialNumber
          }
        />

        <Line
          label={
            I18N.table.submittedAt[lang]
          }
          value={formatDateTime(
            row?.createdAt,
          )}
        />

        <Box sx={{ pt: 0.15 }}>
          <Chip
            size="small"
            label={statusLabel(
              lang,
              status,
            )}
            color={statusChipColor(
              status,
            )}
            sx={{
              fontFamily,
              fontWeight: 700,
            }}
          />
        </Box>
      </Stack>

      <Stack spacing={0.75}>
        <Tooltip
          title={
            I18N.actions.view[lang]
          }
          arrow
        >
          <IconButton
            size="small"
            onClick={onView}
            aria-label="view repair details"
            sx={{
              border:
                "1px solid rgba(0,0,0,0.12)",
              borderRadius: 2,
            }}
          >
            <VisibilityRoundedIcon
              fontSize="small"
            />
          </IconButton>
        </Tooltip>

        {status === "checkedout" ? (
          <Tooltip
            title={
              I18N.actions.cancel[lang]
            }
            arrow
          >
            <IconButton
              size="small"
              onClick={onCancel}
              aria-label="cancel repair request"
              sx={{
                border:
                  "1px solid rgba(211,47,47,0.35)",
                borderRadius: 2,
                color: "error.main",
              }}
            >
              <CancelRoundedIcon
                fontSize="small"
              />
            </IconButton>
          </Tooltip>
        ) : null}
      </Stack>
    </Paper>
  );
}

export default function RetailHistory({
  fonts: providedFonts,
  font,
}) {
  const theme = useTheme();

  const isMobile = useMediaQuery(
    theme.breakpoints.down("sm"),
  );

  const navigate = useNavigate();
  const location = useLocation();

  const {
    user,
    loading: authLoading,
  } = useAuth();

  const safeFonts = useMemo(() => {
    const source =
      providedFonts || font;

    if (
      !source ||
      typeof source !== "object"
    ) {
      return FALLBACK_FONTS;
    }

    return {
      ZH:
        source.ZH ||
        FALLBACK_FONTS.ZH,

      EN:
        source.EN ||
        FALLBACK_FONTS.EN,

      JP:
        source.JP ||
        FALLBACK_FONTS.JP,
    };
  }, [providedFonts, font]);

  const [lang, setLang] = useState(
    () =>
      normalizeLang(
        getLangFromCookie(),
      ),
  );

  const [rows, setRows] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [loadError, setLoadError] =
    useState("");

  const [query, setQuery] =
    useState("");

  const [
    statusFilter,
    setStatusFilter,
  ] = useState("");

  const [dateFrom, setDateFrom] =
    useState("");

  const [dateTo, setDateTo] =
    useState("");

  const [sortKey, setSortKey] =
    useState("submittedAt");

  const [sortDir, setSortDir] =
    useState("desc");

  const [selected, setSelected] =
    useState(null);

  const [
    selectedComments,
    setSelectedComments,
  ] = useState([]);

  const [
    commentsLoading,
    setCommentsLoading,
  ] = useState(false);

  const [
    commentsError,
    setCommentsError,
  ] = useState("");

  /*
   * Used to ignore an older comment request when the user
   * closes the dialog or opens another Retail request.
   */
  const commentsRequestRef =
    useRef(0);

  const [
    cancelTarget,
    setCancelTarget,
  ] = useState(null);

  const [
    cancelLoading,
    setCancelLoading,
  ] = useState(false);

  const [
    cancelError,
    setCancelError,
  ] = useState("");

  const fontFamily =
    safeFonts[lang];

  const userID = useMemo(
    () =>
      resolveWordPressUserID(
        user,
      ) || null,
    [user],
  );

  useEffect(() => {
    const syncLanguage = () => {
      setLang(
        normalizeLang(
          getLangFromCookie(),
        ),
      );
    };

    syncLanguage();

    window.addEventListener(
      "app:languageChanged",
      syncLanguage,
    );

    return () => {
      window.removeEventListener(
        "app:languageChanged",
        syncLanguage,
      );
    };
  }, []);

  useEffect(() => {
    if (
      !authLoading &&
      !user
    ) {
      navigate("/auth", {
        replace: false,

        state: {
          redirectTo:
            location.pathname,
        },
      });
    }
  }, [
    authLoading,
    user,
    navigate,
    location.pathname,
  ]);

  useEffect(() => {
    let active = true;

    const loadHistory = async () => {
      if (authLoading) {
        return;
      }

      if (!userID) {
        if (active) {
          setRows([]);
          setLoading(false);
        }

        return;
      }

      setLoading(true);
      setLoadError("");

      try {
        const result =
          await RetailAPI.getHistory(
            userID,
          );

        if (!active) {
          return;
        }

        setRows(
          Array.isArray(result)
            ? result
            : [],
        );
      } catch (error) {
        if (!active) {
          return;
        }

        console.error(
          "Failed to load Retail history:",
          error,
        );

        setRows([]);

        setLoadError(
          I18N.loadFailed[lang],
        );
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadHistory();

    return () => {
      active = false;
    };
  }, [
    authLoading,
    userID,
    lang,
  ]);

  const filteredSortedRows =
    useMemo(() => {
      const normalizedQuery =
        String(query || "")
          .trim()
          .toLowerCase();

      const fromTime = dateFrom
        ? new Date(
            `${dateFrom}T00:00:00`,
          ).getTime()
        : null;

      const toTime = dateTo
        ? new Date(
            `${dateTo}T23:59:59`,
          ).getTime()
        : null;

      const filtered = rows.filter(
        (row) => {
          const createdDate =
            parseMysqlDateTime(
              row?.createdAt,
            );

          const createdTime =
            createdDate
              ? createdDate.getTime()
              : null;

          const status = String(
            row?.status || "",
          ).toLowerCase();

          const searchableValues = [
            row?.retailID,
            row?.info?.equipmentName,
            displayEquipment(
              row,
              lang,
            ),
            row?.info
              ?.customEquipmentNameOrModel,
            row?.info?.modelName,
            row?.info?.serialNumber,
          ]
            .map((value) =>
              String(
                value || "",
              ).toLowerCase(),
            )
            .join(" | ");

          const matchesQuery =
            !normalizedQuery ||
            searchableValues.includes(
              normalizedQuery,
            );

          const matchesStatus =
            !statusFilter ||
            status === statusFilter;

          const matchesFrom =
            fromTime == null ||
            createdTime == null ||
            createdTime >= fromTime;

          const matchesTo =
            toTime == null ||
            createdTime == null ||
            createdTime <= toTime;

          return (
            matchesQuery &&
            matchesStatus &&
            matchesFrom &&
            matchesTo
          );
        },
      );

      const getSortValue = (
        row,
      ) => {
        if (
          sortKey === "retailID"
        ) {
          return String(
            row?.retailID || "",
          ).toLowerCase();
        }

        if (
          sortKey === "equipment"
        ) {
          return displayEquipment(
            row,
            lang,
          ).toLowerCase();
        }

        if (
          sortKey === "model"
        ) {
          return displayModel(
            row,
          ).toLowerCase();
        }

        if (
          sortKey === "serial"
        ) {
          return String(
            row?.info
              ?.serialNumber || "",
          ).toLowerCase();
        }

        if (
          sortKey === "status"
        ) {
          return statusLabel(
            lang,
            row?.status,
          ).toLowerCase();
        }

        const date =
          parseMysqlDateTime(
            row?.createdAt,
          );

        return date
          ? date.getTime()
          : -Infinity;
      };

      const direction =
        sortDir === "asc"
          ? 1
          : -1;

      return [...filtered].sort(
        (left, right) => {
          const a =
            getSortValue(left);

          const b =
            getSortValue(right);

          if (
            typeof a === "number" &&
            typeof b === "number"
          ) {
            if (a === b) {
              return 0;
            }

            return a > b
              ? direction
              : -direction;
          }

          return (
            String(a).localeCompare(
              String(b),
            ) * direction
          );
        },
      );
    }, [
      rows,
      query,
      statusFilter,
      dateFrom,
      dateTo,
      sortKey,
      sortDir,
      lang,
    ]);

  const toggleSort = (key) => {
    if (sortKey !== key) {
      setSortKey(key);
      setSortDir("asc");
      return;
    }

    setSortDir((current) =>
      current === "asc"
        ? "desc"
        : "asc",
    );
  };

  const SortHeader = ({
    sortName,
    label,
  }) => {
    const active =
      sortKey === sortName;

    const Icon = active
      ? sortDir === "asc"
        ? ArrowUpwardRoundedIcon
        : ArrowDownwardRoundedIcon
      : null;

    return (
      <Stack
        direction="row"
        spacing={0.6}
        alignItems="center"
        role="button"
        tabIndex={0}
        onClick={() =>
          toggleSort(sortName)
        }
        onKeyDown={(event) => {
          if (
            event.key === "Enter" ||
            event.key === " "
          ) {
            event.preventDefault();

            toggleSort(
              sortName,
            );
          }
        }}
        sx={{
          cursor: "pointer",
          userSelect: "none",

          "&:hover .retail-sort-label":
            {
              textDecoration:
                "underline",
            },
        }}
      >
        <Typography
          className="retail-sort-label"
          sx={{
            fontFamily,
            fontWeight: 700,
          }}
        >
          {label}
        </Typography>

        {Icon ? (
          <Icon
            sx={{
              fontSize: 16,
              opacity: 0.85,
            }}
          />
        ) : null}
      </Stack>
    );
  };

  const handleDetailClose = () => {
    /*
     * Invalidate any unfinished comment-loading request.
     */
    commentsRequestRef.current += 1;

    setSelected(null);
    setSelectedComments([]);
    setCommentsLoading(false);
    setCommentsError("");
  };

  const handleView = async (row) => {
    if (!row?.retailID) {
      return;
    }

    const requestID =
      commentsRequestRef.current + 1;

    commentsRequestRef.current =
      requestID;

    setSelected(row);
    setSelectedComments([]);
    setCommentsError("");    
    setCommentsLoading(true);

    if (!userID) {
      setCommentsLoading(false);

      setCommentsError(
        I18N.dialog
          .commentsLoadFailed[lang],
      );

      return;
    }

    try {
      const result =
        await RetailAPI.getComments(
          row.retailID,
          userID,
        );

      if (
        commentsRequestRef.current !==
        requestID
      ) {
        return;
      }

      setSelectedComments(
        Array.isArray(result?.comments)
          ? result.comments
          : [],
      );
    } catch (error) {
      if (
        commentsRequestRef.current !==
        requestID
      ) {
        return;
      }

      console.error(
        "Failed to load Retail comments:",
        error,
      );

      setSelectedComments([]);

      setCommentsError(
        I18N.dialog
          .commentsLoadFailed[lang],
      );
    } finally {
      if (
        commentsRequestRef.current ===
        requestID
      ) {
        setCommentsLoading(false);
      }
    }
  };

  const handleCancelOpen = (
    row,
  ) => {
    if (
      !row ||
      row.status !== "checkedout"
    ) {
      return;
    }

    setCancelError("");
    setCancelTarget(row);
  };

  const handleCancelClose =
    () => {
      if (cancelLoading) {
        return;
      }

      setCancelTarget(null);
      setCancelError("");
    };

  const handleCancelConfirm =
    async () => {
      if (
        !cancelTarget?.retailID ||
        !userID
      ) {
        return;
      }

      setCancelLoading(true);
      setCancelError("");

      try {
        const result =
          await RetailAPI.cancel(
            cancelTarget.retailID,
            userID,
          );

        const updatedItem =
          result?.item || null;

        setRows((currentRows) =>
          currentRows.map((row) => {
            if (
              row.retailID !==
              cancelTarget.retailID
            ) {
              return row;
            }

            return updatedItem
              ? updatedItem
              : {
                  ...row,

                  status:
                    "canceled",

                  updatedAt:
                    result?.updatedAt ||
                    row.updatedAt,
                };
          }),
        );

        setSelected(
          (currentSelected) => {
            if (
              currentSelected
                ?.retailID !==
              cancelTarget.retailID
            ) {
              return currentSelected;
            }

            return updatedItem
              ? updatedItem
              : {
                  ...currentSelected,

                  status:
                    "canceled",

                  updatedAt:
                    result?.updatedAt ||
                    currentSelected.updatedAt,
                };
          },
        );

        setCancelTarget(null);
      } catch (error) {
        console.error(
          "Failed to cancel Retail request:",
          error,
        );

        setCancelError(
          I18N.cancel.failed[lang],
        );
      } finally {
        setCancelLoading(false);
      }
    };

  const selectedPerson =
    selected?.person || {};

  const selectedInfo =
    selected?.info || {};

  return (
    <Box
      sx={{
        width: "100%",

        px: {
          xs: 1.5,
          md: 3,
        },

        py: {
          xs: 2,
          md: 3,
        },
      }}
    >
      <Typography
        variant="h3"
        align="center"
        sx={{
          mb: 1.5,
          color: "#F5A400",
          fontFamily,

          fontSize: {
            xs: 24,
            sm: 30,
          },

          fontWeight: 700,
        }}
      >
        {I18N.title[lang]}
      </Typography>

      <Paper
        variant="outlined"
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1.25,
          mb: 1.5,
          borderRadius: 2,
          p: 1.25,
        }}
      >
        <TextField
          size="small"
          value={query}
          onChange={(event) =>
            setQuery(
              event.target.value,
            )
          }
          placeholder={
            I18N.search
              .placeholder[lang]
          }
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRoundedIcon
                  fontSize="small"
                />
              </InputAdornment>
            ),

            endAdornment: query ? (
              <InputAdornment position="end">
                <Tooltip
                  title={
                    I18N.search
                      .clear[lang]
                  }
                  arrow
                >
                  <IconButton
                    size="small"
                    edge="end"
                    onClick={() =>
                      setQuery("")
                    }
                  >
                    <ClearRoundedIcon
                      fontSize="small"
                    />
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ) : null,
          }}
          sx={{
            width: {
              xs: "100%",
              md: 390,
            },

            "& input": {
              fontFamily,
              fontWeight: 700,
            },
          }}
        />

        <TextField
          select
          size="small"
          label={
            I18N.filter.status[lang]
          }
          value={statusFilter}
          onChange={(event) =>
            setStatusFilter(
              event.target.value,
            )
          }
          sx={{
            width: {
              xs: "100%",
              sm: 180,
            },

            "& .MuiInputBase-input":
              {
                fontFamily,
                fontWeight: 700,
              },
          }}
        >
          <MenuItem value="">
            {
              I18N.filter
                .allStatus[lang]
            }
          </MenuItem>

          <MenuItem value="checkedout">
            {
              I18N.status
                .checkedout[lang]
            }
          </MenuItem>

          <MenuItem value="processing">
            {
              I18N.status
                .processing[lang]
            }
          </MenuItem>

          <MenuItem value="completed">
            {
              I18N.status
                .completed[lang]
            }
          </MenuItem>

          <MenuItem value="canceled">
            {
              I18N.status
                .canceled[lang]
            }
          </MenuItem>
        </TextField>

        <TextField
          size="small"
          type="date"
          label={
            I18N.filter
              .dateFrom[lang]
          }
          value={dateFrom}
          onChange={(event) =>
            setDateFrom(
              event.target.value,
            )
          }
          InputLabelProps={{
            shrink: true,
          }}
          autoComplete="off"
          sx={{
            width: {
              xs: "100%",
              sm: 170,
            },

            "& input": {
              fontFamily,
              fontWeight: 700,
            },
          }}
        />

        <Box
          sx={{
            display: {
              xs: "none",
              sm: "flex",
            },

            alignItems: "center",
            color: "text.secondary",
            fontSize: 18,
            fontWeight: 700,
          }}
        >
          -
        </Box>

        <TextField
          size="small"
          type="date"
          label={
            I18N.filter
              .dateTo[lang]
          }
          value={dateTo}
          onChange={(event) =>
            setDateTo(
              event.target.value,
            )
          }
          InputLabelProps={{
            shrink: true,
          }}
          autoComplete="off"
          sx={{
            width: {
              xs: "100%",
              sm: 170,
            },

            "& input": {
              fontFamily,
              fontWeight: 700,
            },
          }}
        />

        {query ||
        statusFilter ||
        dateFrom ||
        dateTo ? (
          <Button
            variant="outlined"
            onClick={() => {
              setQuery("");
              setStatusFilter("");
              setDateFrom("");
              setDateTo("");
            }}
            sx={{
              height: 40,
              fontFamily,
              fontWeight: 700,
              whiteSpace: "nowrap",
            }}
          >
            {
              I18N.search
                .clear[lang]
            }
          </Button>
        ) : null}
      </Paper>

      {loadError ? (
        <Alert
          severity="error"
          sx={{
            mb: 1.5,
            fontFamily,
          }}
        >
          {loadError}
        </Alert>
      ) : null}

      <Paper
        variant="outlined"
        sx={{
          overflow: "hidden",
          borderRadius: 2,
        }}
      >
        {loading ? (
          <Stack
            direction="row"
            spacing={1.25}
            alignItems="center"
            sx={{ p: 3 }}
          >
            <CircularProgress
              size={22}
            />

            <Typography
              sx={{ fontFamily }}
            >
              {
                I18N.loading[
                  lang
                ]
              }
            </Typography>
          </Stack>
        ) : filteredSortedRows.length ===
          0 ? (
          <Box sx={{ p: 3 }}>
            <Typography
              sx={{ fontFamily }}
            >
              {I18N.empty[lang]}
            </Typography>
          </Box>
        ) : isMobile ? (
          <Stack
            spacing={1.25}
            sx={{ p: 1.5 }}
          >
            {filteredSortedRows.map(
              (row) => (
                <MobileRowCard
                  key={
                    row.retailID
                  }
                  row={row}
                  lang={lang}
                  fontFamily={
                    fontFamily
                  }
                  onView={() =>
                    handleView(row)
                  }
                  onCancel={() =>
                    handleCancelOpen(
                      row,
                    )
                  }
                />
              ),
            )}
          </Stack>
        ) : (
          <TableContainer
            sx={{
              width: "100%",
              overflowX: "auto",
            }}
          >
            <Table
              size="medium"
              sx={{ minWidth: 980 }}
            >
              <TableHead
                sx={{
                  bgcolor:
                    "rgba(0,0,0,0.03)",
                }}
              >
                <TableRow>
                  <TableCell>
                    <SortHeader
                      sortName="retailID"
                      label={
                        I18N.table
                          .retailID[
                          lang
                        ]
                      }
                    />
                  </TableCell>

                  <TableCell>
                    <SortHeader
                      sortName="equipment"
                      label={
                        I18N.table
                          .equipment[
                          lang
                        ]
                      }
                    />
                  </TableCell>

                  <TableCell>
                    <SortHeader
                      sortName="model"
                      label={
                        I18N.table
                          .model[lang]
                      }
                    />
                  </TableCell>

                  <TableCell>
                    <SortHeader
                      sortName="serial"
                      label={
                        I18N.table
                          .serial[
                          lang
                        ]
                      }
                    />
                  </TableCell>

                  <TableCell>
                    <SortHeader
                      sortName="submittedAt"
                      label={
                        I18N.table
                          .submittedAt[
                          lang
                        ]
                      }
                    />
                  </TableCell>

                  <TableCell>
                    <SortHeader
                      sortName="status"
                      label={
                        I18N.table
                          .status[
                          lang
                        ]
                      }
                    />
                  </TableCell>

                  <TableCell
                    align="center"
                    sx={{
                      width: 100,
                      fontFamily,
                      fontWeight: 700,
                    }}
                  >
                    {
                      I18N.table
                        .actions[
                        lang
                      ]
                    }
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {filteredSortedRows.map(
                  (row) => {
                    const status =
                      row.status ||
                      "unknown";

                    return (
                      <TableRow
                        key={
                          row.retailID
                        }
                        hover
                        sx={{
                          "& td": {
                            py: 1.25,
                          },
                        }}
                      >
                        <TableCell
                          sx={{
                            fontFamily,
                            fontWeight: 700,
                          }}
                        >
                          {safe(
                            row.retailID,
                          )}
                        </TableCell>

                        <TableCell
                          sx={{
                            fontFamily,
                          }}
                        >
                          {displayEquipment(
                            row,
                            lang,
                          )}
                        </TableCell>

                        <TableCell
                          sx={{
                            fontFamily,
                          }}
                        >
                          {safe(
                            displayModel(
                              row,
                            ),
                          )}
                        </TableCell>

                        <TableCell
                          sx={{
                            fontFamily,
                          }}
                        >
                          {safe(
                            row.info
                              ?.serialNumber,
                          )}
                        </TableCell>

                        <TableCell
                          sx={{
                            fontFamily,
                          }}
                        >
                          {formatDateTime(
                            row.createdAt,
                          )}
                        </TableCell>

                        <TableCell
                          sx={{
                            fontFamily,
                          }}
                        >
                          <Chip
                            size="small"
                            label={statusLabel(
                              lang,
                              status,
                            )}
                            color={statusChipColor(
                              status,
                            )}
                            sx={{
                              fontFamily,
                              fontWeight: 700,
                            }}
                          />
                        </TableCell>

                        <TableCell align="center">
                          <Stack
                            direction="row"
                            spacing={
                              0.75
                            }
                            justifyContent="center"
                          >
                            <Tooltip
                              title={
                                I18N
                                  .actions
                                  .view[
                                  lang
                                ]
                              }
                              arrow
                            >
                              <IconButton
                                size="small"
                                onClick={() =>
                                  handleView(
                                    row,
                                  )
                                }
                                aria-label="view repair details"
                                sx={{
                                  border:
                                    "1px solid rgba(0,0,0,0.12)",
                                  borderRadius: 2,
                                }}
                              >
                                <VisibilityRoundedIcon
                                  fontSize="small"
                                />
                              </IconButton>
                            </Tooltip>

                            {status ===
                            "checkedout" ? (
                              <Tooltip
                                title={
                                  I18N
                                    .actions
                                    .cancel[
                                    lang
                                  ]
                                }
                                arrow
                              >
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    handleCancelOpen(
                                      row,
                                    )
                                  }
                                  aria-label="cancel repair request"
                                  sx={{
                                    border:
                                      "1px solid rgba(211,47,47,0.35)",
                                    borderRadius: 2,
                                    color:
                                      "error.main",
                                  }}
                                >
                                  <CancelRoundedIcon
                                    fontSize="small"
                                  />
                                </IconButton>
                              </Tooltip>
                            ) : null}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  },
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog
        open={Boolean(
          cancelTarget,
        )}
        onClose={
          handleCancelClose
        }
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent:
              "space-between",
            gap: 2,
            px: 2.5,
            py: 1.75,
            fontFamily,
            fontWeight: 700,
          }}
        >
          {
            I18N.cancel.title[
              lang
            ]
          }

          <IconButton
            size="small"
            onClick={
              handleCancelClose
            }
            disabled={
              cancelLoading
            }
            sx={{
              border:
                "1px solid rgba(0,0,0,0.12)",
              borderRadius: 2,
            }}
          >
            <CloseRoundedIcon
              fontSize="small"
            />
          </IconButton>
        </DialogTitle>

        <Divider />

        <DialogContent
          sx={{ p: 2.5 }}
        >
          <Typography
            sx={{
              mb: 1.5,
              fontFamily,
              fontWeight: 700,
              lineHeight: 1.7,
            }}
          >
            {
              I18N.cancel
                .message[lang]
            }
          </Typography>

          <Paper
            variant="outlined"
            sx={{
              mb: 2,
              borderRadius: 2,
              bgcolor:
                "rgba(0,0,0,0.02)",
              p: 1.5,
            }}
          >
            <Typography
              sx={{
                mb: 0.5,
                color:
                  "text.secondary",
                fontFamily,
                fontSize: 13,
                fontWeight: 700,
              }}
            >
              {
                I18N.table
                  .retailID[lang]
              }
            </Typography>

            <Typography
              sx={{
                fontFamily,
                fontWeight: 700,
                overflowWrap:
                  "anywhere",
              }}
            >
              {safe(
                cancelTarget
                  ?.retailID,
              )}
            </Typography>
          </Paper>

          {cancelError ? (
            <Alert
              severity="error"
              sx={{
                mb: 2,
                fontFamily,
              }}
            >
              {cancelError}
            </Alert>
          ) : null}

          <Stack
            direction="row"
            spacing={1}
            justifyContent="flex-end"
          >
            <Button
              variant="outlined"
              onClick={
                handleCancelClose
              }
              disabled={
                cancelLoading
              }
              sx={{
                fontFamily,
                borderRadius: 2,
              }}
            >
              {
                I18N.actions
                  .close[lang]
              }
            </Button>

            <Button
              variant="contained"
              color="error"
              onClick={
                handleCancelConfirm
              }
              disabled={
                cancelLoading
              }
              startIcon={
                cancelLoading ? (
                  <CircularProgress
                    size={16}
                    color="inherit"
                  />
                ) : null
              }
              sx={{
                fontFamily,
                borderRadius: 2,
              }}
            >
              {
                I18N.cancel
                  .confirm[lang]
              }
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(selected)}
        onClose={handleDetailClose}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent:
              "space-between",
            gap: 2,

            px: {
              xs: 2,
              sm: 2.5,
            },

            py: 1.75,
          }}
        >
          <Typography
            sx={{
              minWidth: 0,
              fontFamily,

              fontSize: {
                xs: 18,
                sm: 20,
              },

              fontWeight: 700,
              overflowWrap:
                "anywhere",
            }}
          >
            {
              I18N.dialog.title[
                lang
              ]
            }

            {selected?.retailID
              ? ` (${selected.retailID})`
              : ""}
          </Typography>

          <IconButton
            size="small"
            onClick={handleDetailClose}
            sx={{
              flexShrink: 0,
              border:
                "1px solid rgba(0,0,0,0.12)",
              borderRadius: 2,
            }}
          >
            <CloseRoundedIcon
              fontSize="small"
            />
          </IconButton>
        </DialogTitle>

        <Divider />

        <DialogContent
          sx={{
            p: {
              xs: 2,
              sm: 2.5,
            },
          }}
        >
          <DetailSection
            title={
              I18N.dialog
                .requestSection[
                lang
              ]
            }
            fontFamily={
              fontFamily
            }
          >
            <Box
              sx={{
                display: "grid",

                gridTemplateColumns:
                  {
                    xs: "1fr",

                    sm:
                      "repeat(2, minmax(0, 1fr))",
                  },

                gap: 2,
              }}
            >
              <KV
                label={
                  I18N.dialog
                    .fields
                    .retailID[
                    lang
                  ]
                }
                value={
                  selected
                    ?.retailID
                }
                fontFamily={
                  fontFamily
                }
              />

              <KV
                label={
                  I18N.dialog
                    .fields
                    .status[lang]
                }
                fontFamily={
                  fontFamily
                }
              >
                <Chip
                  size="small"
                  label={statusLabel(
                    lang,
                    selected?.status,
                  )}
                  color={statusChipColor(
                    selected?.status,
                  )}
                  sx={{
                    fontFamily,
                    fontWeight: 700,
                  }}
                />
              </KV>

              <KV
                label={
                  I18N.dialog
                    .fields
                    .createdAt[
                    lang
                  ]
                }
                value={formatDateTime(
                  selected
                    ?.createdAt,
                )}
                fontFamily={
                  fontFamily
                }
              />

              <KV
                label={
                  I18N.dialog
                    .fields
                    .updatedAt[
                    lang
                  ]
                }
                value={formatDateTime(
                  selected
                    ?.updatedAt,
                )}
                fontFamily={
                  fontFamily
                }
              />
            </Box>
          </DetailSection>

          <DetailSection
            title={
              I18N.dialog
                .contactSection[
                lang
              ]
            }
            fontFamily={
              fontFamily
            }
          >
            <Box
              sx={{
                display: "grid",

                gridTemplateColumns:
                  {
                    xs: "1fr",

                    sm:
                      "repeat(2, minmax(0, 1fr))",

                    md:
                      "repeat(3, minmax(0, 1fr))",
                  },

                gap: 2,
              }}
            >
              <KV
                label={
                  I18N.dialog
                    .fields
                    .name[lang]
                }
                value={
                  selectedPerson.name
                }
                fontFamily={
                  fontFamily
                }
              />

              <KV
                label={
                  I18N.dialog
                    .fields
                    .usageLocation[
                    lang
                  ]
                }
                value={
                  selectedPerson
                    .usageLocation
                }
                fontFamily={
                  fontFamily
                }
              />

              <KV
                label={
                  I18N.dialog
                    .fields
                    .endUser[
                    lang
                  ]
                }
                value={
                  selectedPerson
                    .endUser
                }
                fontFamily={
                  fontFamily
                }
              />

              <KV
                label={
                  I18N.dialog
                    .fields
                    .repairAvailableTime[
                    lang
                  ]
                }
                value={
                  selectedPerson
                    .repairAvailableTime
                }
                fontFamily={
                  fontFamily
                }
              />

              <KV
                label={
                  I18N.dialog
                    .fields
                    .landline[
                    lang
                  ]
                }
                value={buildLandline(
                  selectedPerson,
                )}
                fontFamily={
                  fontFamily
                }
              />

              <KV
                label={
                  I18N.dialog
                    .fields
                    .mobile[lang]
                }
                value={
                  selectedPerson
                    .mobileNumber
                }
                fontFamily={
                  fontFamily
                }
              />

              <KV
                label={
                  I18N.dialog
                    .fields
                    .email[lang]
                }
                value={
                  selectedPerson.email
                }
                fontFamily={
                  fontFamily
                }
              />

              <KV
                label={
                  I18N.dialog
                    .fields
                    .address[
                    lang
                  ]
                }
                value={buildAddress(
                  selectedPerson,
                )}
                fontFamily={
                  fontFamily
                }
              />
            </Box>
          </DetailSection>

          <DetailSection
            title={
              I18N.dialog
                .equipmentSection[
                lang
              ]
            }
            fontFamily={
              fontFamily
            }
          >
            <Box
              sx={{
                display: "grid",

                gridTemplateColumns:
                  {
                    xs: "1fr",

                    sm:
                      "repeat(2, minmax(0, 1fr))",

                    md:
                      "repeat(3, minmax(0, 1fr))",
                  },

                gap: 2,
              }}
            >
              <KV
                label={
                  I18N.dialog
                    .fields
                    .equipmentName[
                    lang
                  ]
                }
                value={translatedLabel(
                  EQUIPMENT_LABELS,
                  selectedInfo
                    .equipmentName,
                  lang,
                )}
                fontFamily={
                  fontFamily
                }
              />

              <KV
                label={
                  I18N.dialog
                    .fields
                    .customEquipment[
                    lang
                  ]
                }
                value={
                  selectedInfo
                    .customEquipmentNameOrModel
                }
                fontFamily={
                  fontFamily
                }
              />

              <KV
                label={
                  I18N.dialog
                    .fields
                    .modelName[
                    lang
                  ]
                }
                value={
                  selectedInfo
                    .modelName
                }
                fontFamily={
                  fontFamily
                }
              />

              <KV
                label={
                  I18N.dialog
                    .fields
                    .serialNumber[
                    lang
                  ]
                }
                value={
                  selectedInfo
                    .serialNumber
                }
                fontFamily={
                  fontFamily
                }
              />

              <KV
                label={
                  I18N.dialog
                    .fields
                    .purchaseDate[
                    lang
                  ]
                }
                value={formatDateOnly(
                  selectedInfo
                    .purchaseDate,
                )}
                fontFamily={
                  fontFamily
                }
              />

              <KV
                label={
                  I18N.dialog
                    .fields
                    .shippingDate[
                    lang
                  ]
                }
                value={formatDateOnly(
                  selectedInfo
                    .shippingDate,
                )}
                fontFamily={
                  fontFamily
                }
              />

              <KV
                label={
                  I18N.dialog
                    .fields
                    .installationDate[
                    lang
                  ]
                }
                value={formatDateOnly(
                  selectedInfo
                    .installationDate,
                )}
                fontFamily={
                  fontFamily
                }
              />

              <KV
                label={
                  I18N.dialog
                    .fields
                    .installer[
                    lang
                  ]
                }
                value={
                  selectedInfo
                    .installer
                }
                fontFamily={
                  fontFamily
                }
              />

              <KV
                label={
                  I18N.dialog
                    .fields
                    .installationSource[
                    lang
                  ]
                }
                value={translatedLabel(
                  INSTALLATION_SOURCE_LABELS,
                  selectedInfo
                    .installationSource,
                  lang,
                )}
                fontFamily={
                  fontFamily
                }
              />

              <KV
                label={
                  I18N.dialog
                    .fields
                    .customInstallationSource[
                    lang
                  ]
                }
                value={
                  selectedInfo
                    .customInstallationSource
                }
                fontFamily={
                  fontFamily
                }
              />
            </Box>
          </DetailSection>

          <DetailSection
            title={
              I18N.dialog
                .faultSection[
                lang
              ]
            }
            fontFamily={
              fontFamily
            }
          >
            <Box
              sx={{
                display: "grid",

                gridTemplateColumns:
                  {
                    xs: "1fr",

                    sm:
                      "repeat(2, minmax(0, 1fr))",
                  },

                gap: 2,
              }}
            >
              <KV
                label={
                  I18N.dialog
                    .fields
                    .faultReason[
                    lang
                  ]
                }
                value={translatedLabel(
                  FAULT_REASON_LABELS,
                  selectedInfo
                    .faultReason,
                  lang,
                )}
                fontFamily={
                  fontFamily
                }
              />

              <KV
                label={
                  I18N.dialog
                    .fields
                    .customFaultReason[
                    lang
                  ]
                }
                value={
                  selectedInfo
                    .customFaultReason
                }
                fontFamily={
                  fontFamily
                }
                multiline
              />

              <Box
                sx={{
                  gridColumn: {
                    sm: "1 / -1",
                  },
                }}
              >
                <KV
                  label={
                    I18N.dialog
                      .fields
                      .repairNotes[
                      lang
                    ]
                  }
                  value={
                    selectedInfo
                      .repairNotes
                  }
                  fontFamily={
                    fontFamily
                  }
                  multiline
                />
              </Box>
            </Box>
          </DetailSection>

          <DetailSection
            title={
              I18N.dialog
                .commentsSection[
                lang
              ]
            }
            fontFamily={
              fontFamily
            }
          >
            {commentsLoading ? (
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ py: 1 }}
              >
                <CircularProgress
                  size={20}
                />

                <Typography
                  sx={{
                    fontFamily,
                    fontSize: 14,
                  }}
                >
                  {
                    I18N.dialog
                      .commentsLoading[
                      lang
                    ]
                  }
                </Typography>
              </Stack>
            ) : commentsError ? (
              <Alert
                severity="error"
                sx={{
                  fontFamily,
                }}
              >
                {commentsError}
              </Alert>
            ) : selectedComments.length ===
              0 ? (
              <Typography
                sx={{
                  color:
                    "text.secondary",
                  fontFamily,
                  fontSize: 14,
                }}
              >
                {
                  I18N.dialog
                    .commentsEmpty[
                    lang
                  ]
                }
              </Typography>
            ) : (
              <Stack spacing={1.25}>
                {selectedComments.map(
                  (
                    comment,
                    index,
                  ) => (
                    <Paper
                      key={
                        comment
                          .commentID ||
                        [
                          comment
                            .createdAt,
                          index,
                        ].join("-")
                      }
                      variant="outlined"
                      sx={{
                        borderRadius: 2,
                        bgcolor:
                          "#ffffff",

                        p: {
                          xs: 1.5,
                          sm: 1.75,
                        },
                      }}
                    >
                      <Typography
                        sx={{
                          mb: 0.75,
                          color:
                            "text.secondary",
                          fontFamily,
                          fontSize: 12,
                          fontWeight: 600,
                          textAlign:
                            "right",
                        }}
                      >
                        {formatDateTime(
                          comment
                            .createdAt,
                        )}
                      </Typography>

                      <Typography
                        sx={{
                          fontFamily,
                          fontSize: 14,
                          fontWeight: 500,
                          lineHeight: 1.75,
                          overflowWrap:
                            "anywhere",
                          whiteSpace:
                            "pre-wrap",
                        }}
                      >
                        {safe(
                          comment
                            .commentContent,
                        )}
                      </Typography>
                    </Paper>
                  ),
                )}
              </Stack>
            )}


          </DetailSection>

          <Stack
            direction="row"
            justifyContent="flex-end"
          >
            <Button
              variant="outlined"
              onClick={handleDetailClose}
              sx={{
                fontFamily,
                borderRadius: 2,
                px: 2.5,
              }}
            >
              {
                I18N.actions
                  .close[lang]
              }
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>
    </Box>
  );
}